'use client';

import { c as _c } from "react/compiler-runtime";
import { getApiErrorMessage, useApi } from '@repo/api';
import { useAuth } from '@repo/auth';
import { type CompanySearchResult, fromCompanySearchResult } from '@context/companies';
import { useLastOrganisation, useOrganisationScope } from '@repo/features/organisations';
import { isLoadsListPath } from '@/app/@protected/loads/_utils/loads-paths.ts';
import { useLoadCreationAccess } from '@/app/@protected/loads/_utils/use-load-creation-access.ts';
import { NewLoadCompanyRiskBanner } from '@/app/@protected/loads/new/_components/company-risk-banner/company-risk-banner.tsx';
import { useCreateCompanySheet } from '@/app/@protected/loads/new/_components/create-company/context.tsx';
import { CreateCompanySheetNestedDialogOutlet } from '@/app/@protected/loads/new/_components/create-company/sheet.tsx';
import { CreateLocationSheetNestedDialogOutlet } from '@/app/@protected/_components/create-location';
import { useLoadLocationHandlers } from '@/app/@protected/loads/_hooks/use-load-location-handlers.ts';
import { buildEndCustomerCompanyQueryInput } from '@/app/@protected/loads/new/_form/_utils/form-helpers.ts';
import { getDefaultLoadOrganisationId } from '@/app/@protected/loads/new/_form/_hooks/use-organisation-form-state.ts';
import { getEndCompanyOrganisationId } from '@/app/@protected/loads/_utils/end-company-organisation.ts';
import { useDebouncedValue } from '@repo/tanstack/pacer';
import { useMutation, useQuery, useQueryClient } from '@repo/tanstack/query';
import { toast } from '@repo/ui/components';
import { createContext, type PropsWithChildren, use, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { type QuickCreateCompany, QuickCreateLoadDialog, type QuickCreateLoadDialogProps } from './quick-create-load-dialog.tsx';
import { mapQuickCreateToPublishedLoad } from './_utils/publish-mapper.ts';
import { isPublishableQuickCreateValues } from './_utils/publish-values.ts';
import type { QuickCreateValues } from './_utils/values.ts';
import { useLoadCreatedToast } from '../../loads/_hooks/use-load-created-toast.tsx';
type QuickCreateContextValue = {
  open: () => void;
};
const QuickCreateContext = createContext<null | QuickCreateContextValue>(null);
export const useQuickCreateLoad = () => {
  const context = use(QuickCreateContext);
  if (!context) throw new Error('useQuickCreateLoad must be used within QuickCreateLoadHost');
  return context;
};
const asQuickCompany = (company: CompanySearchResult): QuickCreateCompany => fromCompanySearchResult(company, {
  includeRisk: true
});
export const QuickCreateLoadHost = t0 => {
  const $ = _c(113);
  const {
    children
  } = t0;
  const api = useApi();
  const {
    user
  } = useAuth();
  const {
    canQuickCreate
  } = useLoadCreationAccess();
  const {
    scope
  } = useOrganisationScope();
  const {
    lastOrganisationId,
    rememberOrganisation
  } = useLastOrganisation();
  const pathname = usePathname();
  const {
    openCreateCompanySheet
  } = useCreateCompanySheet();
  const {
    handleCreateLocation,
    handleEditLocation
  } = useLoadLocationHandlers();
  const queryClient = useQueryClient();
  const {
    showLoadCreatedToast
  } = useLoadCreatedToast();
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== lastOrganisationId || $[1] !== scope) {
    t1 = () => getDefaultLoadOrganisationId({
      lastOrganisationId,
      scope
    });
    $[0] = lastOrganisationId;
    $[1] = scope;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [selectedOrganisationId, setSelectedOrganisationId] = useState(t1);
  const hasExplicitOrganisationSelection = useRef(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [publishError, setPublishError] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [endCustomerSearch, setEndCustomerSearch] = useState("");
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      wait: 300
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [customerSearchValue] = useDebouncedValue(customerSearch, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      wait: 300
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const [endCustomerSearchValue] = useDebouncedValue(endCustomerSearch, t3);
  const organisationId = selectedOrganisationId;
  let t4;
  if ($[5] !== lastOrganisationId || $[6] !== scope) {
    t4 = () => {
      hasExplicitOrganisationSelection.current = false;
      setPublishError(null);
      setSelectedOrganisationId(getDefaultLoadOrganisationId({
        lastOrganisationId,
        scope
      }));
      setSelectedCustomer(null);
      setCustomerSearch("");
      setEndCustomerSearch("");
    };
    $[5] = lastOrganisationId;
    $[6] = scope;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const resetSessionOrganisation = t4;
  let t5;
  if ($[8] !== canQuickCreate || $[9] !== open || $[10] !== resetSessionOrganisation) {
    t5 = () => {
      if (!canQuickCreate || open) {
        return;
      }
      resetSessionOrganisation();
      setOpen(true);
    };
    $[8] = canQuickCreate;
    $[9] = open;
    $[10] = resetSessionOrganisation;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const openQuickCreate = t5;
  let t6;
  let t7;
  if ($[12] !== lastOrganisationId || $[13] !== open || $[14] !== scope) {
    t6 = () => {
      if (open || hasExplicitOrganisationSelection.current) {
        return;
      }
      setSelectedOrganisationId(getDefaultLoadOrganisationId({
        lastOrganisationId,
        scope
      }));
    };
    t7 = [lastOrganisationId, open, scope];
    $[12] = lastOrganisationId;
    $[13] = open;
    $[14] = scope;
    $[15] = t6;
    $[16] = t7;
  } else {
    t6 = $[15];
    t7 = $[16];
  }
  useEffect(t6, t7);
  const operationalOrganisationId = getEndCompanyOrganisationId(selectedCustomer ? {
    is_marketplace: selectedCustomer.isMarketplace,
    represents_organisation_id: selectedCustomer.representsOrganisationId
  } : null, organisationId);
  let t8;
  if ($[17] !== api.organisations.list) {
    t8 = api.organisations.list.queryOptions();
    $[17] = api.organisations.list;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const organisations = useQuery(t8);
  let t9;
  if ($[19] !== api.companies.search || $[20] !== customerSearchValue || $[21] !== open || $[22] !== organisationId) {
    t9 = api.companies.search.queryOptions({
      input: {
        "filter[type]": "shipper,broker",
        org_id: organisationId,
        perPage: 20,
        search: customerSearchValue
      }
    }, {
      enabled: open && organisationId != null
    });
    $[19] = api.companies.search;
    $[20] = customerSearchValue;
    $[21] = open;
    $[22] = organisationId;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const customers = useQuery(t9);
  const endCustomers = useQuery(api.companies.search.queryOptions({
    input: {
      ...buildEndCustomerCompanyQueryInput({
        filterOrganisationId: operationalOrganisationId,
        orgId: organisationId,
        search: endCustomerSearchValue
      })
    }
  }, {
    enabled: open && operationalOrganisationId != null
  }));
  let t10;
  if ($[24] !== api.loads.create) {
    t10 = api.loads.create.mutationOptions({
      meta: {
        invalidates: [["loads"]]
      }
    });
    $[24] = api.loads.create;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const {
    isPending,
    mutateAsync
  } = useMutation(t10);
  let t11;
  if ($[26] !== api.loads.list || $[27] !== canQuickCreate || $[28] !== mutateAsync || $[29] !== pathname || $[30] !== queryClient || $[31] !== showLoadCreatedToast) {
    t11 = async values => {
      if (!canQuickCreate) {
        return;
      }
      setPublishError(null);
      if (!isPublishableQuickCreateValues(values)) {
        setPublishError("Unable to create load");
        toast.error("Unable to create load");
        return;
      }
      ;
      try {
        const result = await mutateAsync(mapQuickCreateToPublishedLoad(values));
        setOpen(false);
        showLoadCreatedToast(result.id);
        if (isLoadsListPath(pathname)) {
          Promise.resolve(queryClient.invalidateQueries({
            queryKey: api.loads.list.allScopesKey({
              input: {}
            })
          })).catch(_temp);
        }
      } catch (t12) {
        const error = t12;
        const title = "Unable to create load";
        const message_0 = getApiErrorMessage(error, "Unable to create load")[0]?.message ?? "Unable to create load";
        setPublishError(message_0);
        toast.error("Unable to create load", {
          description: message_0 === "Unable to create load" ? undefined : message_0
        });
      }
    };
    $[26] = api.loads.list;
    $[27] = canQuickCreate;
    $[28] = mutateAsync;
    $[29] = pathname;
    $[30] = queryClient;
    $[31] = showLoadCreatedToast;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const publish = t11;
  let t12;
  if ($[33] !== user) {
    t12 = user ? {
      id: user.id
    } : null;
    $[33] = user;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== customers.data?.data) {
    t13 = customers.data?.data ?? [];
    $[35] = customers.data?.data;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t13) {
    t14 = t13.map(asQuickCompany);
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== customers.error) {
    t15 = customers.error ? getApiErrorMessage(customers.error)[0]?.message : null;
    $[39] = customers.error;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== customers) {
    t16 = () => void customers.refetch();
    $[41] = customers;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  const t17 = customers.isPending ? "loading" : "ready";
  let t18;
  if ($[43] !== t14 || $[44] !== t15 || $[45] !== t16 || $[46] !== t17) {
    t18 = {
      companies: t14,
      error: t15,
      onRetry: t16,
      onSearchChange: setCustomerSearch,
      status: t17
    };
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  const t19 = selectedCustomer?.id;
  let t20;
  if ($[48] !== t19) {
    t20 = <NewLoadCompanyRiskBanner className="animate-in flex-row flex-wrap justify-start gap-x-3 gap-y-2 px-3 py-2 text-left duration-150 ease-out fade-in-0" companyId={t19} />;
    $[48] = t19;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== endCustomers.data?.data || $[51] !== selectedCustomer?.id) {
    let t22;
    if ($[53] !== selectedCustomer?.id) {
      t22 = company => company.id !== selectedCustomer?.id;
      $[53] = selectedCustomer?.id;
      $[54] = t22;
    } else {
      t22 = $[54];
    }
    t21 = (endCustomers.data?.data ?? []).filter(t22).map(asQuickCompany);
    $[50] = endCustomers.data?.data;
    $[51] = selectedCustomer?.id;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[55] !== endCustomers.error) {
    t22 = endCustomers.error ? getApiErrorMessage(endCustomers.error)[0]?.message : null;
    $[55] = endCustomers.error;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== endCustomers) {
    t23 = () => void endCustomers.refetch();
    $[57] = endCustomers;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  const t24 = endCustomers.isPending ? "loading" : "ready";
  let t25;
  if ($[59] !== t21 || $[60] !== t22 || $[61] !== t23 || $[62] !== t24) {
    t25 = {
      companies: t21,
      error: t22,
      onRetry: t23,
      onSearchChange: setEndCustomerSearch,
      status: t24
    };
    $[59] = t21;
    $[60] = t22;
    $[61] = t23;
    $[62] = t24;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  const t26 = organisationId ?? null;
  let t27;
  if ($[64] !== t26) {
    t27 = {
      organisation_id: t26
    };
    $[64] = t26;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <><CreateCompanySheetNestedDialogOutlet /><CreateLocationSheetNestedDialogOutlet /></>;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== openCreateCompanySheet || $[68] !== operationalOrganisationId || $[69] !== organisationId) {
    t29 = request => {
      const isEndCustomer = request.kind === "end-customer";
      openCreateCompanySheet({
        ...(isEndCustomer ? {
          allowedTypes: ["shipper", "broker"] as const,
          defaultType: "shipper" as const,
          inheritedOrganisationId: operationalOrganisationId ?? organisationId
        } : {
          inheritedOrganisationId: request.organisationId ?? organisationId
        }),
        initialValues: {
          title: request.search
        },
        onCreated: company_0 => {
          request.onCreated({
            id: company_0.id,
            isBroker: company_0.type === "broker",
            title: company_0.title
          });
        },
        presentation: "nested-dialog"
      });
    };
    $[67] = openCreateCompanySheet;
    $[68] = operationalOrganisationId;
    $[69] = organisationId;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== handleCreateLocation || $[72] !== operationalOrganisationId) {
    t30 = request_0 => void handleCreateLocation({
      ...request_0,
      orgIdOverride: operationalOrganisationId,
      presentation: "nested-dialog"
    });
    $[71] = handleCreateLocation;
    $[72] = operationalOrganisationId;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] !== handleEditLocation || $[75] !== operationalOrganisationId) {
    t31 = request_1 => handleEditLocation({
      ...request_1,
      orgIdOverride: operationalOrganisationId,
      presentation: "nested-dialog"
    });
    $[74] = handleEditLocation;
    $[75] = operationalOrganisationId;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  let t32;
  if ($[77] !== openQuickCreate || $[78] !== resetSessionOrganisation) {
    t32 = nextOpen => {
      if (nextOpen) {
        openQuickCreate();
        return;
      }
      resetSessionOrganisation();
      setOpen(nextOpen);
    };
    $[77] = openQuickCreate;
    $[78] = resetSessionOrganisation;
    $[79] = t32;
  } else {
    t32 = $[79];
  }
  let t33;
  if ($[80] !== rememberOrganisation) {
    t33 = nextId => {
      hasExplicitOrganisationSelection.current = true;
      setSelectedOrganisationId(nextId ?? undefined);
      setSelectedCustomer(null);
      setCustomerSearch("");
      setEndCustomerSearch("");
      if (nextId != null) {
        rememberOrganisation(nextId);
      }
    };
    $[80] = rememberOrganisation;
    $[81] = t33;
  } else {
    t33 = $[81];
  }
  const t34 = scope.kind === "all";
  let t35;
  if ($[82] !== organisations.data) {
    t35 = organisations.data ?? [];
    $[82] = organisations.data;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  let t36;
  if ($[84] !== t35) {
    t36 = t35.map(_temp2);
    $[84] = t35;
    $[85] = t36;
  } else {
    t36 = $[85];
  }
  let t37;
  if ($[86] !== t34 || $[87] !== t36) {
    t37 = {
      editable: t34,
      options: t36
    };
    $[86] = t34;
    $[87] = t36;
    $[88] = t37;
  } else {
    t37 = $[88];
  }
  let t38;
  if ($[89] !== isPending || $[90] !== open || $[91] !== publish || $[92] !== publishError || $[93] !== t12 || $[94] !== t18 || $[95] !== t20 || $[96] !== t25 || $[97] !== t27 || $[98] !== t29 || $[99] !== t30 || $[100] !== t31 || $[101] !== t32 || $[102] !== t33 || $[103] !== t37) {
    t38 = {
      currentUser: t12,
      customerCompanies: t18,
      customerRiskBanner: t20,
      endCustomerCompanies: t25,
      initialValues: t27,
      nestedSheetOutlets: t28,
      onCreateCompany: t29,
      onCreateLocation: t30,
      onCustomerChange: setSelectedCustomer,
      onEditLocation: t31,
      onOpenChange: t32,
      onOrganisationChange: t33,
      onPublish: publish,
      open,
      organisation: t37,
      pending: isPending,
      publishError
    };
    $[89] = isPending;
    $[90] = open;
    $[91] = publish;
    $[92] = publishError;
    $[93] = t12;
    $[94] = t18;
    $[95] = t20;
    $[96] = t25;
    $[97] = t27;
    $[98] = t29;
    $[99] = t30;
    $[100] = t31;
    $[101] = t32;
    $[102] = t33;
    $[103] = t37;
    $[104] = t38;
  } else {
    t38 = $[104];
  }
  const dialogProps = t38;
  let t39;
  if ($[105] !== openQuickCreate) {
    t39 = {
      open: openQuickCreate
    };
    $[105] = openQuickCreate;
    $[106] = t39;
  } else {
    t39 = $[106];
  }
  let t40;
  if ($[107] !== dialogProps) {
    t40 = <QuickCreateLoadDialog {...dialogProps} data-sentry-element="QuickCreateLoadDialog" data-sentry-source-file="quick-create-load-host.tsx" />;
    $[107] = dialogProps;
    $[108] = t40;
  } else {
    t40 = $[108];
  }
  let t41;
  if ($[109] !== children || $[110] !== t39 || $[111] !== t40) {
    t41 = <QuickCreateContext.Provider value={t39} data-sentry-element="QuickCreateContext.Provider" data-sentry-component="QuickCreateLoadHost" data-sentry-source-file="quick-create-load-host.tsx">{children}{t40}</QuickCreateContext.Provider>;
    $[109] = children;
    $[110] = t39;
    $[111] = t40;
    $[112] = t41;
  } else {
    t41 = $[112];
  }
  return t41;
};
function _temp() {}
function _temp2(item) {
  return {
    id: item.id,
    name: item.name
  };
}